/**
 * The action bar — one button per gameplay window, docked in the bottom-left
 * corner.
 *
 * Janeks, 2026-10-16: *"Move the Menu buttons to bottom-left corner (where
 * debug is now). Make the world chat not a menu button but a elongated 'world
 * chat' UI button."* So the bar is no longer a slot inside the player frame
 * (`hud.ts`) but its own dock, and an entry marked `wide` renders as a long
 * labelled button under the row of squares instead of as one of them. Chat
 * stays an entry in `ACTION_WINDOWS` all the same: that table is what the T
 * key is read from, and splitting it out would be the button/shortcut drift
 * the next section is about.
 *
 * Janeks: *"All of the different windows for gameplay to be opened via
 * separate button + key … add buttons, similar to WoW, right under the Player
 * icon block. Square, with icon and stylistic border + tooltips, when tutorial
 * mode is on or hovered."*
 *
 * ─── One definition drives both the button and the key ──────────────────────
 * Each window arrives as an `ActionBarItem` carrying its own `toggle`,
 * `isOpen` and `enabled`. `index.ts` builds that list ONCE and hands it to
 * both this bar and its own `keydown` handler, so a button and its shortcut
 * cannot drift apart. That matters more than it sounds: before this, every
 * binding carried its own hand-copied
 * `!dialog.isOpen && !isVendorUIOpen() && …` chain — five copies of a
 * predicate, one of which had already been written differently from the rest.
 *
 * ─── Two ways to show the same tooltip ──────────────────────────────────────
 * Hovering shows a floating tooltip. Tutorial mode shows the SAME text for
 * every button at once — which a floating tooltip cannot do, because four of
 * them under four 44 px squares land on top of each other. So tutorial mode
 * switches the bar to captions that sit in normal flow under each button and
 * widen the cells to fit. One content source, two layouts; nothing is
 * duplicated but the CSS.
 *
 * ─── Icons are inline SVG ───────────────────────────────────────────────────
 * Drawn with `currentColor` strokes so hover/active/disabled recolour the
 * glyph with the button and no second asset is needed. Same reasoning as
 * `render/quest-markers.ts`: generated beats shipped for a handful of glyphs,
 * and it keeps them testable as strings.
 */
import { tutorialStore } from '../../state/tutorial-state';
import { actionArtHtml, hudArtUrl } from './hud-art';

export interface ActionBarItem {
  /** Stable id — used for the `data-action` hook and in tests. */
  id: string;
  /** Tooltip/caption text. */
  label: string;
  /**
   * The keyboard shortcut, lowercase and single-character. Shown as the
   * keycap badge, and `index.ts` matches `KeyboardEvent.key` against it — so
   * this is the one place the binding is written down.
   */
  key: string;
  /** One of `ACTION_ICONS`. */
  icon: keyof typeof ACTION_ICONS;
  /** Appended to the tooltip in parentheses, e.g. `WIP`. */
  note?: string;
  /**
   * Render as a long labelled button under the row of squares rather than as
   * a square in it. Only World Chat — see the header.
   */
  wide?: boolean;
  /** Is this window currently showing? Drives the pressed state. */
  isOpen(): boolean;
  /** Open it if closed, close it if open. */
  toggle(): void;
  /**
   * False while another surface owns the screen. Each item carries its own
   * gate rather than sharing one, because the existing gates genuinely
   * differ — the player record opens over a vendor panel, the map does not.
   */
  enabled(): boolean;
  /**
   * A count painted on the button's corner, hidden at 0. Optional: only the
   * journal has one (its active quests).
   */
  badge?(): number;
}

/**
 * 24x24, stroke-only, `currentColor`. Stroke rather than fill so they stay
 * readable at 20 px against the dark brass frame.
 */
export const ACTION_ICONS = {
  /** A backpack: body, lid seam, and the two shoulder straps. */
  backpack: '<path d="M8 9V7a4 4 0 0 1 8 0v2"/><rect x="4.5" y="9" width="15" height="11.5" rx="3"/>'
    + '<path d="M4.5 14h15"/><path d="M10.5 17h3"/>',
  /**
   * A ruled page — the quest log.
   *
   * Was an open book, which at 22 px was near-indistinguishable from the
   * folded map two buttons along: both read as a wide shape creased down the
   * middle. Caught by rendering the bar, not by any test. A portrait page with
   * ruled lines shares no silhouette with anything else on the bar.
   */
  journal: '<rect x="5.5" y="3.6" width="13" height="16.8" rx="2"/>'
    + '<path d="M8.8 8.4h6.4"/><path d="M8.8 12h6.4"/><path d="M8.8 15.6h3.6"/>',
  /** A price tag: the body, and the punched hole the string goes through. */
  tag: '<path d="M4.8 11.6V5.4A.6.6 0 0 1 5.4 4.8h6.2a1 1 0 0 1 .7.3l7 7a1 1 0 0 1 0 1.4l-6.2 6.2'
    + 'a1 1 0 0 1-1.4 0l-7-7a1 1 0 0 1-.3-.7Z"/><circle cx="8.6" cy="8.6" r="1.4"/>',
  /** A folded map: the outline, and the two folds that make it read as paper. */
  map: '<path d="M9 5.2 3.8 7.4v11.4L9 16.6l6 2.2 5.2-2.2V5.2L15 7.4Z"/>'
    + '<path d="M9 5.2v11.4"/><path d="M15 7.4v11.4"/>',
  /**
   * Crossed blades — the arena.
   *
   * Two diagonals with a guard and a pommel on each, rather than the obvious
   * "two crossed lines": at 22 px a bare X is the close button, which is the
   * one glyph a launcher button must not be mistaken for.
   */
  swords: '<path d="M5 4.4h2.2l9.4 9.4"/><path d="M19 4.4h-2.2L7.4 13.8"/>'
    + '<path d="m6.2 16.4 1.8 1.8"/><path d="m17.8 16.4-1.8 1.8"/>'
    + '<path d="M4.4 18.2 6.6 20.4"/><path d="M19.6 18.2 17.4 20.4"/>',
  /**
   * A pitched roof over a doorway — the Home.
   *
   * A house outline with the door drawn IN, not a bare roof triangle: at 22 px
   * a triangle over a square is close enough to the map glyph's folded paper,
   * and the door is the one stroke that says "you go inside this one".
   */
  house: '<path d="M3.8 10.6 12 4.2l8.2 6.4"/>'
    + '<path d="M5.8 12.2v7.4a.8.8 0 0 0 .8.8h10.8a.8.8 0 0 0 .8-.8v-7.4"/>'
    + '<path d="M10 20.4v-5.2h4v5.2"/>',
  /** A speech bubble with a tail, and two lines of talk inside it. */
  speech: '<path d="M4.6 6.4a2 2 0 0 1 2-2h10.8a2 2 0 0 1 2 2v7.2a2 2 0 0 1-2 2H10l-4.2 3.4v-3.4h-1.2Z"/>'
    + '<path d="M8.4 8.8h7.2"/><path d="M8.4 12h4.4"/>',
} as const;

/** One icon as a complete `<svg>` string. Pure — see the header. */
export function actionIconSvg(icon: keyof typeof ACTION_ICONS): string {
  return '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor"'
    + ` stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${ACTION_ICONS[icon]}</svg>`;
}

/**
 * The tooltip/caption text for one item.
 *
 * One function so the hover tooltip and the tutorial caption can never say
 * different things about the same button.
 *
 * Deliberately does NOT repeat the shortcut: the keycap badge is painted on
 * the button in both modes, so "Quest Journal — J" said it twice — and at the
 * ~57 px a cell gets inside the 268 px player frame, the suffix was enough to
 * wrap every caption onto two lines. `actionAriaLabel` still spells it out,
 * because a screen reader gets no badge.
 */
export function actionTooltip(item: Pick<ActionBarItem, 'label' | 'note'>): string {
  return item.note ? `${item.label} (${item.note})` : item.label;
}

/** The accessible name — the tooltip plus the shortcut the badge shows visually. */
export function actionAriaLabel(item: Pick<ActionBarItem, 'label' | 'key' | 'note'>): string {
  return `${actionTooltip(item)} — ${item.key.toUpperCase()}`;
}

/** The windows, as data. See `ACTION_WINDOWS`. */
export type ActionWindowId = 'inventory' | 'journal' | 'home' | 'shop' | 'map' | 'arena' | 'chat';

export type ActionWindow = Pick<ActionBarItem, 'label' | 'key' | 'icon' | 'note' | 'wide'> & {
  id: ActionWindowId;
};

/**
 * WHAT each button is — its name, its shortcut, its glyph.
 *
 * Split from the behaviour (`isOpen`/`toggle`/`enabled`, which `index.ts`
 * supplies, because only it holds the panels) so that the part a typo would
 * ruin is plain data a test can read. A wrong letter here is invisible: the
 * button still renders, the key still appears to work, and it simply opens
 * nothing — there is no error to notice.
 *
 * Order is render order, left to right.
 */
export const ACTION_WINDOWS: readonly ActionWindow[] = [
  { id: 'inventory', label: 'Inventory', key: 'i', icon: 'backpack' },
  { id: 'journal', label: 'Quest Journal', key: 'j', icon: 'journal' },
  // H was free, checked against every list that reads a keypress: this table
  // (I, J, P, M, V, T), index.ts (WASD/arrows, E, R) and player-controls.ts
  // (RMB, CapsLock, C).
  { id: 'home', label: 'Home', key: 'h', icon: 'house' },
  // The WIP note is GONE (2026-09-20, on request). It was added when the
  // catalog worked but had no placed vendor or stall; the black market is now
  // a finished surface — tabs, cards, the three standing offers, the rolling
  // ammo allowance — and a button that calls itself unfinished undersells it.
  // `note` itself stays: it is the honest label for the next half-built thing.
  { id: 'shop', label: 'Shop', key: 'p', icon: 'tag' },
  { id: 'map', label: 'Map', key: 'm', icon: 'map' },
  // V and T were the two free letters. Taken, checked against BOTH lists that
  // read a keypress — this file's own bindings (I, J, P, M) plus index.ts
  // (WASD/arrows, E, R) and player-controls.ts (RMB, CapsLock, C). C is
  // crouch, which is why chat is not on it.
  { id: 'arena', label: 'Arena', key: 'v', icon: 'swords' },
  // Wide: the long "World Chat" button under the squares (Janeks, 2026-10-16).
  { id: 'chat', label: 'World Chat', key: 't', icon: 'speech', wide: true },
];

/** The half `index.ts` owns: what the button actually does. */
export type ActionWindowBehaviour = Pick<ActionBarItem, 'isOpen' | 'toggle' | 'enabled' | 'badge'>;

/** Joins the two halves into the list both the bar and the key handler read. */
export function bindActionWindows(
  behaviours: Record<ActionWindowId, ActionWindowBehaviour>,
): ActionBarItem[] {
  return ACTION_WINDOWS.map((w) => ({ ...w, ...behaviours[w.id] }));
}

export interface ActionBar {
  readonly el: HTMLDivElement;
  /**
   * Repaints pressed/disabled state. Called from the frame loop — it diffs
   * against what is already in the DOM and writes nothing when unchanged, so
   * a 60 Hz call costs a handful of boolean compares.
   */
  update(): void;
  dispose(): void;
}

interface Cell {
  item: ActionBarItem;
  button: HTMLButtonElement;
  lastOpen: boolean | null;
  lastEnabled: boolean | null;
  count: HTMLSpanElement | null;
  lastBadge: number | null;
}

export function createActionBar(items: readonly ActionBarItem[]): ActionBar {
  const el = document.createElement('div');
  el.className = 'bv-ab-dock';
  // The plate art used to arrive from `.bv-hud`, which set it for the whole
  // player column the bar sat in. Out of that column, the dock sets its own.
  for (const [prop, art] of [['--hud-btn', 'btn_plate'], ['--hud-plate', 'hud_plate']] as const) {
    const url = hudArtUrl(art);
    if (url) el.style.setProperty(prop, `url(${url})`);
  }
  const row = document.createElement('div');
  row.className = 'bv-ab';
  el.appendChild(row);

  const cells: Cell[] = [];
  for (const item of items) {
    if (item.wide) {
      cells.push(wideCell(item, el));
      continue;
    }
    const cell = document.createElement('div');
    cell.className = 'bv-ab-cell';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bv-ab-btn';
    button.dataset.action = item.id;
    // The accessible name, and the browser's own tooltip as a free fallback
    // for anyone who lands here before the styled one renders.
    button.title = actionAriaLabel(item);
    button.setAttribute('aria-label', actionAriaLabel(item));
    button.innerHTML = `${actionArtHtml(item.icon) || actionIconSvg(item.icon)}<span class="bv-ab-key">${item.key.toUpperCase()}</span>`;
    button.addEventListener('click', () => {
      // Re-checked at click time, not just painted: `disabled` is repainted
      // from the frame loop, so a click landing in the same frame a panel
      // opens would otherwise slip through.
      if (item.enabled()) item.toggle();
    });

    const tip = document.createElement('span');
    tip.className = 'bv-ab-tip';
    tip.textContent = actionTooltip(item);

    cell.append(button, tip);
    let count: HTMLSpanElement | null = null;
    if (item.badge) {
      count = document.createElement('span');
      count.className = 'bv-ab-count';
      count.dataset.badge = item.id;
      count.hidden = true;
      cell.appendChild(count);
    }
    row.appendChild(cell);
    cells.push({ item, button, lastOpen: null, lastEnabled: null, count, lastBadge: null });
  }

  const unsubTutorial = tutorialStore.subscribe((state) => {
    el.classList.toggle('bv-ab-teaching', state.enabled);
  });

  // The dock's height, published on its parent so the things that also live
  // in the bottom-left — the chat box, the dev debug panel, the interaction
  // prompt on a phone — stand on top of it instead of under it. Measured
  // rather than summed: the row wraps on a narrow screen and tutorial mode
  // adds captions, and both change the height. No ResizeObserver in jsdom;
  // there is no layout to publish there anyway.
  const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
    el.parentElement?.style.setProperty('--bv-ab-dock-h', `${el.offsetHeight}px`);
  });
  resize?.observe(el);

  return {
    el,
    update(): void {
      for (const cell of cells) {
        const open = cell.item.isOpen();
        if (open !== cell.lastOpen) {
          cell.lastOpen = open;
          cell.button.classList.toggle('bv-ab-btn-active', open);
          cell.button.setAttribute('aria-pressed', String(open));
        }
        const enabled = cell.item.enabled();
        if (enabled !== cell.lastEnabled) {
          cell.lastEnabled = enabled;
          cell.button.disabled = !enabled;
        }
        if (cell.count && cell.item.badge) {
          const n = cell.item.badge();
          if (n !== cell.lastBadge) {
            cell.lastBadge = n;
            cell.count.hidden = n <= 0;
            cell.count.textContent = n > 99 ? '99+' : String(n);
            // The count is part of the button's name, or a screen reader never hears it.
            cell.button.setAttribute('aria-label', n > 0
              ? `${actionAriaLabel(cell.item)} (${n} active)`
              : actionAriaLabel(cell.item));
          }
        }
      }
    },
    dispose(): void {
      unsubTutorial();
      resize?.disconnect();
      el.parentElement?.style.removeProperty('--bv-ab-dock-h');
      el.remove();
    },
  };
}

/**
 * The long labelled button (World Chat). Its label is on its face, so it has
 * no tooltip and no tutorial caption — the two things a square needs because
 * it has only a picture.
 */
function wideCell(item: ActionBarItem, dock: HTMLElement): Cell {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'bv-ab-wide';
  button.dataset.action = item.id;
  button.title = actionAriaLabel(item);
  button.setAttribute('aria-label', actionAriaLabel(item));
  button.innerHTML = `${actionArtHtml(item.icon) || actionIconSvg(item.icon)}`
    + `<span class="bv-ab-wide-label">${actionTooltip(item)}</span>`
    + `<span class="bv-ab-key">${item.key.toUpperCase()}</span>`;
  button.addEventListener('click', () => {
    if (item.enabled()) item.toggle();
  });
  dock.appendChild(button);
  return { item, button, lastOpen: null, lastEnabled: null, count: null, lastBadge: null };
}
